/**
 * ONE declaration of which product licence each worker-portal surface needs.
 *
 * ── WHY THIS FILE EXISTS ─────────────────────────────────────────────────────────────────────────
 *
 * Owner 2026-09-04, after an audit found three routes with no product boundary: 方针是一步到位 …
 * 针对不同产品线许可做的，耦合和可维护性！！！！
 *
 * The licence requirement used to be written in TWO places in this repo that could not see each
 * other — the sidebar's guard expressions in `WorkerLayout.tsx` and the `product=` prop on each
 * `<Route>` in `App.tsx` — plus a third in the backend. 17 surfaces × 2 frontend declarations is 34
 * places to keep in step, and they did not stay in step: `/me/tasks`, `/me/requests/new` and
 * `/me/requests/:id` shipped with a sidebar guard and NO boundary, so an unlicensed worker who typed
 * the URL got a page that rendered and then errored instead of a clean redirect.
 *
 * That is svdy-ui §17's pattern exactly — a value every call site writes will drift — and the fix is
 * the same one: put it in one place and derive. A drift TEST (which is what shipped first) detects the
 * problem; a single declaration removes it.
 *
 * ── THE SHAPE, and why it is a flat map rather than a nav config ─────────────────────────────────
 *
 * Deliberately ONLY the licence. Not labels, not icons, not sections, not oversight — those already
 * have single sources (`NAV_ICON`, the locale files, `buildNavSections`' own structure) and folding
 * them in here would be the config-driven-navigation rewrite nobody asked for. One concern, one map.
 *
 * `null` means the surface is genuinely product-free: a profile, the org chart, the dashboard shell.
 * It is NOT "unknown" — an absent key is a bug this module's own test catches.
 *
 * ── THE BACKEND KEEPS ITS OWN, AND THAT IS CORRECT ──────────────────────────────────────────────
 *
 * `svdy-platform/app/core/licensing.py` `ME_ROUTE_LICENSE_REQUIREMENTS` is the AUTHORITY: it decides
 * whether the API answers, and it must fail closed independently of any client. Sharing a literal
 * across a Python service and a TypeScript SPA would need codegen, which for ~30 prefixes costs more
 * than it saves. What is worth having is a test that the two agree, and with this file there is now
 * ONE frontend declaration to compare instead of two —
 * `licenceGateLayers.test.ts` does that comparison.
 */

export type WorkerProduct = 'qutime' | 'pureoa';

/** Paths are the ROUTED paths, so a reader can grep either the sidebar or App.tsx and land here. */
export const WORKER_SURFACE_PRODUCT: Record<string, WorkerProduct | null> = {
  // Product-free. Every worker has an identity and an employer, whatever the org bought.
  '/me/dashboard': null,
  '/me/organization': null,
  '/me/org-chart': null,
  '/me/profile': null,
  '/me/security': null,

  // QuTime — attendance.
  '/me/schedule': 'qutime',
  '/me/time-entries': 'qutime',
  '/me/leave': 'qutime',
  '/me/holidays': 'qutime',
  '/me/attendance-requests': 'qutime',
  '/me/attendance-approvals': 'qutime',
  '/me/clock-in-reviews': 'qutime',
  '/me/face-reviews': 'qutime',
  '/me/face-template': 'qutime',

  // PureOA — process. The two sub-routes are listed explicitly rather than inherited from
  // `/me/requests`, because that inheritance is exactly what the backend does by PREFIX and what this
  // repo cannot do implicitly — react-router matches them as separate routes, so a missing entry here
  // is a missing boundary there. Both shipped ungated once already.
  '/me/requests': 'pureoa',
  '/me/requests/new': 'pureoa',
  '/me/requests/:id': 'pureoa',
  '/me/tasks': 'pureoa',
  '/me/approvals': 'pureoa',

  // MIXED, and resolved by making the licence follow the CONTENT rather than the page.
  //
  // `/me/team` is a direct-reports roster (employment: who reports to me, their position, how to
  // reach them) with today's punch columns bolted on. The backend deliberately serves it to any
  // licence — `list_team`'s own docstring says *"My direct reports" is EMPLOYMENT, not attendance* —
  // while the sidebar used to gate the whole page on `hasQutime`, so a PureOA-only manager could not
  // reach a roster the API would have given them.
  //
  // Picking either licence for the whole page is wrong in one direction or the other. So the page is
  // product-free and the ATTENDANCE PARTS of it (the clock-in/out/status columns and the summary
  // strip) are gated inside `WorkerTeam.tsx` on `hasQutime`. A PureOA manager gets their team; a
  // QuTime manager gets their team and their day; nobody gets a column that cannot be filled.
  '/me/team': null,
};

/** Does this org's entitlement satisfy the surface? `null`-product surfaces always pass. */
export function surfaceLicensed(
  path: string,
  has: { qutime: boolean; pureoa: boolean },
): boolean {
  const product = WORKER_SURFACE_PRODUCT[path];
  if (product === undefined) {
    // Fail CLOSED on an unknown path. A surface added without an entry here must be invisible rather
    // than accidentally universal — the safe direction when a narrowing input cannot be resolved
    // (session-discipline §12).
    return false;
  }
  if (product === null) return true;
  return product === 'qutime' ? has.qutime : has.pureoa;
}
